import type { Enemy, ContagionState } from "../entities/Enemy.ts";
import type { VirusSynergyMods } from "../entities/types.ts";
import type { VirusProfile } from "./VirusLevels.ts";
import { isStealthHidden } from "./StealthGuard.ts";

const WILDFIRE_FIRE_TICK_INTERVAL = 500;

function clamp01(value: number): number {
  return Math.max(0, Math.min(1, value));
}

/** Infect every active, revealed enemy within `p.zoneRadius` of (x,y).
 *  Shared by initial impact and hazard pools so both paths stay identical. */
export function infectEnemiesInRadius(
  x: number,
  y: number,
  p: VirusProfile,
  enemies: Enemy[],
  mods?: VirusSynergyMods | null,
  goldSporeRadius?: number,
): number {
  const radiusSq = p.zoneRadius * p.zoneRadius;
  let infected = 0;

  for (const enemy of enemies) {
    if (!enemy.active) continue;
    if (isStealthHidden(enemy)) continue;

    const dx = enemy.x - x;
    const dy = enemy.y - y;
    if (dx * dx + dy * dy > radiusSq) continue;

    const contagion: ContagionState = {
      duration: p.duration,
      spreadRadius: p.spreadRadius * (mods?.spreadRadiusMultiplier ?? 1),
      spreadChance: clamp01(p.spreadChance + (mods?.spreadChanceBonus ?? 0)),
      maxGenerations: p.maxGenerations + (mods?.maxGenerationsBonus ?? 0),
      spreadInterval: p.spreadInterval,
      spreadTimer: p.spreadInterval,
      speedDebuff: p.speedDebuff,
    };
    // BL-175 / D-266 — GOLD "PANDEMIC": tag the infection so the foe's death
    // bursts a spore cloud (WaveManager death scan). Undefined ⇒ untagged.
    if (goldSporeRadius !== undefined) {
      contagion.goldSporeRadius = goldSporeRadius;
    }
    enemy.applyDot(
      p.tickDamage,
      p.duration,
      p.tickInterval,
      "virus",
      0,
      contagion,
    );
    if (mods?.fireDot) {
      enemy.applyDot(
        mods.fireDot.tickDamage,
        mods.fireDot.duration,
        WILDFIRE_FIRE_TICK_INTERVAL,
        "fire",
        0,
      );
    }
    infected++;
  }

  return infected;
}

/** BL-175 / D-266 (S170) — virus's GOLD signature "PANDEMIC · Patient Zero". When
 *  an enemy carrying a GOLD-tagged contagion DIES, it bursts a spore cloud that
 *  re-infects every live, revealed, uninfected foe within `goldSporeRadius` px —
 *  the plague becomes self-sustaining across the lane. Wholly deterministic (a
 *  radius scan that COPIES the carrier's contagion at one generation younger so it
 *  keeps spreading; no `rng()`). No-op for an untagged corpse (the byte-identical
 *  default: no fixture infects from a GOLD tower, so no contagion is ever tagged).
 *  Returns the number of foes freshly infected (for the VFX event). */
export function burstPandemicSpores(corpse: Enemy, enemies: Enemy[]): number {
  const dot = corpse.dots.get("virus");
  if (!dot) return 0;
  const c = dot.contagion;
  if (!c || c.goldSporeRadius === undefined) return 0;

  const rSq = c.goldSporeRadius * c.goldSporeRadius;
  let infected = 0;
  for (const enemy of enemies) {
    if (!enemy.active || enemy === corpse) continue;
    if (isStealthHidden(enemy)) continue;
    if (enemy.dots.has("virus")) continue; // never re-infect

    const dx = enemy.x - corpse.x;
    const dy = enemy.y - corpse.y;
    if (dx * dx + dy * dy > rSq) continue;

    // Reset the spread generation to 0 so the burst keeps the plague going (the
    // self-sustaining intent), but carry the GOLD tag forward so the chain
    // re-bursts on the next death too.
    enemy.applyDot(dot.damage, c.duration, dot.tickInterval, "virus", 0, {
      ...c,
      spreadTimer: c.spreadInterval,
    });
    infected++;
  }
  return infected;
}
